import {
  applyMutation,
  emptyListsState,
  mergeItem,
  mergeList,
  MutationSchema,
  type ChangeEvent,
  type ListsState,
  type MeDto,
  type MutationEnvelope,
  type MutationInput,
  type MutationResult,
  type SnapshotDto,
} from "@home/shared";
import { ApiError, NetworkError, type EventStreamHandlers } from "../api.ts";
import type { CachedState, SyncStorage } from "./storage.ts";

export interface Transport {
  snapshot(): Promise<SnapshotDto>;
  mutations(batch: MutationEnvelope[]): Promise<{ results: MutationResult[] }>;
  streamEvents(since: string, handlers: EventStreamHandlers, signal: AbortSignal): Promise<void>;
}

export type ConnectionStatus = "loading" | "online" | "syncing" | "offline" | "unauthorized";

export interface SyncView {
  status: ConnectionStatus;
  me: MeDto | null;
  /** Состояние для отображения: сервер + неотправленные мутации. */
  state: ListsState;
  pending: number;
  /** Последняя мутация, отклонённая сервером (код ошибки). */
  rejected: string | null;
}

const BATCH = 50;
const RETRY_MIN_MS = 2000;
const RETRY_MAX_MS = 30000;
/** Сервер шлёт пинг каждые 25 с; тишина дольше — соединение мертво (прокси, смена сети). */
const STREAM_SILENCE_MS = 40000;

function uuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export { uuid as newId };

/**
 * Движок синхронизации списков.
 * - мутации сразу применяются локально и кладутся в очередь (IndexedDB);
 * - очередь отправляется пачками, при обрыве сети — повтор с паузой;
 * - изменения других членов семьи приходят через SSE и сливаются по version;
 * - видимое состояние = состояние сервера + очередь, применённая поверх.
 */
export class SyncEngine {
  private cached: CachedState = { server: emptyListsState(), cursor: "0", me: null };
  private outbox: MutationEnvelope[] = [];
  private view: SyncView = { status: "loading", me: null, state: emptyListsState(), pending: 0, rejected: null };
  private listeners = new Set<() => void>();
  private flushing = false;
  private retryMs = RETRY_MIN_MS;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private stream: AbortController | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  constructor(
    private readonly transport: Transport,
    private readonly storage: SyncStorage,
    private readonly now: () => string = () => new Date().toISOString(),
  ) {}

  // ───── API для React (useSyncExternalStore) ─────

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getView = (): SyncView => this.view;

  private emit(patch: Partial<SyncView> = {}) {
    const me = this.cached.me;
    let state = this.cached.server;
    if (me) {
      for (const env of this.outbox) state = applyMutation(state, env.mutation, { userId: me.userId, now: this.now() });
    }
    this.view = { ...this.view, ...patch, me, state, pending: this.outbox.length };
    for (const l of this.listeners) l();
  }

  private setStatus(status: ConnectionStatus) {
    if (this.view.status !== status) this.emit({ status });
  }

  // ───── Жизненный цикл ─────

  async start(): Promise<void> {
    const [cached, outbox] = await Promise.all([this.storage.loadState(), this.storage.loadOutbox()]);
    if (cached) this.cached = cached;
    this.outbox = outbox;
    // С кэшем показываем данные сразу; «Нет сети» — только если запрос действительно не прошёл.
    this.emit({ status: cached ? "syncing" : "loading" });
    await this.resync();
  }

  stop(): void {
    this.stopped = true;
    this.stream?.abort();
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  /** Полная пересинхронизация: отправить очередь, взять снапшот, подписаться на события. */
  async resync(): Promise<void> {
    if (this.stopped) return;
    try {
      await this.flush();
      const snap = await this.transport.snapshot();
      this.cached = {
        server: {
          lists: Object.fromEntries(snap.lists.map((l) => [l.id, l])),
          items: Object.fromEntries(snap.items.map((i) => [i.id, i])),
        },
        cursor: snap.cursor,
        me: snap.me,
      };
      await this.storage.saveState(this.cached);
      this.retryMs = RETRY_MIN_MS;
      this.emit({ status: this.outbox.length ? "syncing" : "online" });
      this.connect();
    } catch (err) {
      this.handleError(err);
    }
  }

  private handleError(err: unknown) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      this.emit({ status: "unauthorized", rejected: err.code });
      return;
    }
    this.setStatus("offline");
    this.scheduleRetry();
  }

  private scheduleRetry() {
    if (this.retryTimer || this.stopped) return;
    const delay = this.retryMs;
    this.retryMs = Math.min(this.retryMs * 2, RETRY_MAX_MS);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.resync();
    }, delay);
  }

  /** Вызывать при событиях online/visibilitychange — пробуем сразу, не дожидаясь паузы. */
  wake(): void {
    if (this.view.status === "online" || this.view.status === "unauthorized") return;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.retryMs = RETRY_MIN_MS;
    void this.resync();
  }

  private connect() {
    this.stream?.abort();
    const controller = new AbortController();
    this.stream = controller;

    let watchdog: ReturnType<typeof setTimeout> | null = null;
    const fail = (err: unknown) => {
      if (watchdog) clearTimeout(watchdog);
      if (controller.signal.aborted) return;
      controller.abort();
      this.handleError(err);
    };
    const alive = () => {
      if (watchdog) clearTimeout(watchdog);
      watchdog = setTimeout(() => fail(new NetworkError("stream silent")), STREAM_SILENCE_MS);
    };
    alive();

    this.transport
      .streamEvents(
        this.cached.cursor,
        {
          onChange: (e) => this.applyRemote(e),
          onReady: () => this.setStatus(this.outbox.length ? "syncing" : "online"),
          onActivity: alive,
        },
        controller.signal,
      )
      .then(
        () => fail(new NetworkError("stream closed")),
        (err) => fail(err),
      );
  }

  // ───── Входящие изменения ─────

  applyRemote(event: ChangeEvent): void {
    const server = event.entity === "list" ? mergeList(this.cached.server, event.data) : mergeItem(this.cached.server, event.data);
    this.cached = { ...this.cached, server, cursor: BigInt(event.id) > BigInt(this.cached.cursor) ? event.id : this.cached.cursor };
    this.emit();
    this.scheduleSave();
  }

  private scheduleSave() {
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.storage.saveState(this.cached);
    }, 300);
  }

  // ───── Исходящие мутации ─────

  async mutate(input: MutationInput): Promise<void> {
    const mutation = MutationSchema.parse(input);
    const env: MutationEnvelope = { mutationId: uuid(), mutation };
    this.outbox.push(env);
    this.emit({ rejected: null });
    await this.storage.enqueue(env);
    if (this.view.status === "online" || this.view.status === "syncing") {
      this.setStatus("syncing");
      void this.flush().then(
        () => this.setStatus(this.outbox.length ? "syncing" : "online"),
        (err) => this.handleError(err),
      );
    }
  }

  /** Отправляет очередь пачками. Ошибка сети пробрасывается — вызывающий решает про повтор. */
  async flush(): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    try {
      while (this.outbox.length > 0) {
        const batch = this.outbox.slice(0, BATCH);
        const { results } = await this.transport.mutations(batch);
        let server = this.cached.server;
        let rejected: string | null = null;
        for (const r of results) {
          if (r.ok) {
            for (const l of r.lists) server = mergeList(server, l);
            for (const i of r.items) server = mergeItem(server, i);
          } else {
            rejected = r.error;
          }
        }
        const done = new Set(results.map((r) => r.mutationId));
        this.outbox = this.outbox.filter((e) => !done.has(e.mutationId));
        this.cached = { ...this.cached, server };
        await this.storage.remove([...done]);
        await this.storage.saveState(this.cached);
        this.emit(rejected ? { rejected } : {});
      }
    } finally {
      this.flushing = false;
    }
  }
}
